import { useEffect, useRef, useState } from "react"

import useIsDesktop from "@hooks/useIsDesktop"

interface UseDatePickerOpenStateArgs {
    controlledOpen?: boolean
    onOpenChange: (open: boolean) => void
}

export function useDatePickerOpenState(args: UseDatePickerOpenStateArgs): [boolean, (open: boolean) => void] {
    const { controlledOpen, onOpenChange } = args

    const [isOpen, setIsOpen] = useState<boolean>(typeof controlledOpen === "boolean" ? controlledOpen : false)
    const hasMountedRef = useRef(false)
    const isDesktop = useIsDesktop()

    // Sync internal state when `controlledOpen` changes
    useEffect(() => {
        if (typeof controlledOpen === "boolean") {
            setIsOpen(controlledOpen)
        }
    }, [controlledOpen])

    // On mobile, blur the active element when the drawer opens to dismiss the keyboard
    useEffect(() => {
        if (!isDesktop && isOpen) {
            if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur()
            }
        }
    }, [isOpen, isDesktop])

    // Notify the caller when open state changes (skipping the initial mount)
    useEffect(() => {
        if (!hasMountedRef.current) {
            hasMountedRef.current = true
            return
        }
        onOpenChange(isOpen)
    }, [isOpen, onOpenChange])

    return [isOpen, setIsOpen]
}
